package org.moxultimate.topwithmox.dashboard

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.px
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.texts.DescriptionText
import org.moxultimate.topwithmox.components.texts.TitleText
import org.moxultimate.topwithmox.core.toggle.FeatureToggle.DASHBOARD_TECH_TOGGLE
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.statics.DashboardStatics.COMING_SOON
import org.moxultimate.topwithmox.statics.DashboardStatics.COMING_SOON_DISPLAY
import org.moxultimate.topwithmox.statics.DashboardStatics.HOW_TO_PREVIEW_1_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.HOW_TO_PREVIEW_1_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.HOW_TO_PREVIEW_2_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.HOW_TO_PREVIEW_2_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.HOW_TO_PREVIEW_3_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.HOW_TO_PREVIEW_3_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.TECH_BUTTON
import org.moxultimate.topwithmox.statics.DashboardStatics.TECH_DESCRIPTION
import org.moxultimate.topwithmox.statics.DashboardStatics.TECH_LATEST_TEXT
import org.moxultimate.topwithmox.statics.DashboardStatics.TECH_TITLE
import org.moxultimate.topwithmox.tech.data.TechResponse
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.utils.trackDashboardCardClick

/**
 * Builds the preview list from live API data when available, falling back to static placeholders.
 * Each entry is Triple(imageUrl, title, clickUrl). Live items use the tech entry's videoLink.
 *
 * @param techs Live tech list from the dashboard API, or null when still loading.
 * @return List of (imageUrl, title, clickUrl) triples for the 3 preview cards.
 */
private data class TechPreviewItem(
    val imageUrl: String,
    val title: String,
    val type: String,
    val clickUrl: String,
)

private fun buildTechPreviews(techs: List<TechResponse>?): List<TechPreviewItem> =
    if (!techs.isNullOrEmpty()) {
        techs.map { tech -> TechPreviewItem(tech.thumbnailPath ?: "", tech.title ?: "", tech.type ?: "", tech.videoLink ?: "") }
    } else {
        listOf(
            TechPreviewItem(HOW_TO_PREVIEW_1_URL, HOW_TO_PREVIEW_1_TITLE, "", ""),
            TechPreviewItem(HOW_TO_PREVIEW_2_URL, HOW_TO_PREVIEW_2_TITLE, "", ""),
            TechPreviewItem(HOW_TO_PREVIEW_3_URL, HOW_TO_PREVIEW_3_TITLE, "", ""),
        )
    }

/**
 * TechSection
 * Dashboard section showcasing Top Tech & Guides on a dark starfield background.
 * Cards are clickable — opens the item's videoLink in a new tab.
 *
 * @param techs Live list of the 3 most recent tech entries from the dashboard API.
 *              Null while loading — falls back to static placeholder cards.
 */
@OptIn(DelicateApi::class)
@Composable
fun TechSection(techs: List<TechResponse>? = null) {
    val breakpoint = rememberBreakpoint()
    val previews = buildTechPreviews(techs)

    when (breakpoint) {
        Breakpoint.ZERO, Breakpoint.SM, Breakpoint.MD -> TechSectionMobile(previews)
        else -> TechSectionDesktop(previews)
    }
}

/**
 * TechSectionDesktop
 * Starfield section with a 3-column grid of [TechPreviewCard]s.
 *
 * @param previews Resolved [TechPreviewItem] list — live data or static fallback.
 */
@Composable
private fun TechSectionDesktop(previews: List<TechPreviewItem>) {
    val navigator = rememberAppNavigator()

    StarfieldSectionFrameDesktop(showCurvedBottom = DASHBOARD_TECH_TOGGLE) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            TitleText(title = TECH_TITLE)
            GradientButton(
                text = if (DASHBOARD_TECH_TOGGLE) TECH_BUTTON else COMING_SOON,
                buttonHeight = 50,
                disabled = !DASHBOARD_TECH_TOGGLE,
                onClick = { navigator.goToTechPage() },
            )
        }

        DescriptionText(modifier = Modifier.padding(top = 16.px), description = TECH_DESCRIPTION)

        if (DASHBOARD_TECH_TOGGLE) {
            DescriptionText(
                description = TECH_LATEST_TEXT,
                modifier = Modifier.padding(top = 40.px, bottom = 16.px),
            )

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier { property("flex-wrap", "wrap") },
                horizontalArrangement = Arrangement.spacedBy(24.px),
                verticalAlignment = Alignment.Top,
            ) {
                previews.forEach { item ->
                    TechPreviewCard(
                        item = item,
                        modifier = Modifier
                            .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                            .margin(bottom = 16.px),
                    )
                }
            }
        } else {
            Box(
                modifier = Modifier.fillMaxWidth().padding(top = 100.px, bottom = 50.px),
                contentAlignment = Alignment.Center,
            ) {
                TitleText(title = COMING_SOON_DISPLAY, modifier = Modifier.textAlign(TextAlign.Center))
            }
        }
    }
}

/**
 * TechPreviewCard
 * Clickable 16:9 tech thumbnail. On hover: image scales + brightens, title grows and turns
 * gradient, type subtitle appears in gradient. Opens clickUrl in a new tab on click.
 *
 * @param item     All display and interaction data for this card.
 * @param modifier Outer modifier — caller controls width and margin for grid placement.
 */
@Composable
private fun TechPreviewCard(
    item: TechPreviewItem,
    modifier: Modifier = Modifier,
) {
    var isHovered by remember { mutableStateOf(false) }
    val isClickable = item.clickUrl.isNotBlank()

    Column(
        modifier = modifier
            .attrsModifier {
                onMouseEnter { isHovered = true }
                onMouseLeave { isHovered = false }
                if (isClickable) onClick {
                    trackDashboardCardClick("tech", item.title, item.clickUrl)
                    window.open(item.clickUrl, "_blank")
                }
            }
            .styleModifier {
                if (isClickable) property("cursor", "pointer")
            },
        horizontalAlignment = Alignment.Start,
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(12.px)
                .overflow(Overflow.Hidden)
                .styleModifier { property("aspect-ratio", "16 / 9") },
        ) {
            Image(
                src = item.imageUrl,
                description = item.title,
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight()
                    .styleModifier {
                        property("object-fit", "cover")
                        property("display", "block")
                        property("transition", "transform 0.3s ease, filter 0.3s ease")
                        property("transform", if (isHovered && isClickable) "scale(1.06)" else "scale(1.0)")
                        property("filter", if (isHovered && isClickable) "brightness(1.1)" else "brightness(1.0)")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
        }

        SpanText(
            text = item.title,
            modifier = Modifier
                .padding(top = 10.px)
                .color(Colors.White)
                .fontFamily(FontFamily)
                .fontSize(13.px)
                .fontWeight(FontWeight.SemiBold)
                .styleModifier {
                    property("display", "inline-block")
                    property("transition", "transform 0.2s ease")
                    property("transform", if (isHovered && isClickable) "scale(1.12)" else "scale(1.0)")
                    property("transform-origin", "left center")
                },
        )

        if (item.type.isNotBlank()) {
            SpanText(
                text = item.type,
                modifier = Modifier
                    .padding(top = 4.px)
                    .color(Colors.White)
                    .fontFamily(FontFamily)
                    .fontSize(12.px)
                    .fontWeight(FontWeight.Normal),
            )
        }
    }
}


//----- Tech Section Mobile --------------------------------------------------------------------------------------------


/**
 * TechSectionMobile
 * Starfield section: stacked title/description, horizontally scrollable card row, CTA button.
 *
 * @param previews Resolved (imageUrl, title, clickUrl) triples — live data or static fallback.
 */
@OptIn(DelicateApi::class)
@Composable
private fun TechSectionMobile(previews: List<TechPreviewItem>) {
    val navigator = rememberAppNavigator()

    StarfieldSectionFrameMobile {
        Column(
            modifier = Modifier.fillMaxWidth().padding(leftRight = 40.px, bottom = 32.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            TitleText(title = TECH_TITLE, modifier = Modifier.textAlign(TextAlign.Center))
            DescriptionText(
                modifier = Modifier.padding(top = 24.px).textAlign(TextAlign.Center),
                description = TECH_DESCRIPTION,
            )
        }

        if (DASHBOARD_TECH_TOGGLE) {
            DescriptionText(
                description = TECH_LATEST_TEXT,
                modifier = Modifier.padding(left = 24.px, bottom = 12.px),
            )

            Row(
                modifier = WallpaperCarouselStyle.toModifier().fillMaxWidth().padding(leftRight = 16.px),
                horizontalArrangement = Arrangement.spacedBy(12.px),
                verticalAlignment = Alignment.Top,
            ) {
                previews.forEach { item ->
                    TechPreviewCard(
                        item = item,
                        modifier = Modifier.styleModifier {
                            property("width", "85vw")
                            property("flex-shrink", "0")
                        },
                    )
                }
            }
        }

        Box(
            modifier = Modifier.fillMaxWidth().padding(top = 32.px, bottom = 8.px),
            contentAlignment = Alignment.Center,
        ) {
            GradientButton(
                text = if (DASHBOARD_TECH_TOGGLE) TECH_BUTTON else COMING_SOON,
                disabled = !DASHBOARD_TECH_TOGGLE,
                onClick = { navigator.goToTechPage() },
            )
        }
    }
}
